import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Calendar, 
  Settings, 
  Save, 
  AlertCircle, 
  CheckCircle,
  ToggleLeft,
  ToggleRight,
  Percent,
  MessageSquare,
  Calculator,
  CreditCard as CreditCardIcon
} from 'lucide-react';
import { paymentOptionsAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const PaymentOptionsManagement = () => {
  const { notify } = useNotificationHook();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [config, setConfig] = useState({
    payment_options_enabled: true,
    full_payment_enabled: true,
    advance_payment_enabled: true,
    advance_payment_percentage: 50,
    advance_payment_after_demo_percentage: 25,
    advance_payment_after_project_percentage: 25,
    full_payment_coupon_code: 'FUL20',
    full_payment_coupon_discount: 20,
    full_payment_coupon_message: 'Use coupon code FUL20 to get 20% discount on full payment',
    advance_payment_instructions: 'Split: 50% | After Demo Completion: 25% | After Project Completion: 25%',
    payment_options_title: 'Choose Your Payment Option',
    payment_options_description: 'Select your preferred payment method and schedule',
    emi_payment_enabled: true,
    emi_minimum_amount: 1000,
    emi_tenure_months: '3,6,9,12',
    emi_interest_rate: 15,
    emi_processing_fee: 99,
    emi_eligibility_message: 'EMI available on orders above ₹1000',
    emi_calculator_title: 'EMI Calculator',
    emi_calculator_description: 'Calculate your monthly EMI amount (Flat Rate Method)',
    emi_terms_conditions: 'EMI is subject to bank approval. Processing fee of ₹99 applies. Interest rate: 15% p.a. (Flat Rate Method).',
    emi_bank_partners: 'HDFC, ICICI, SBI, Axis, Kotak',
    emi_required_documents: 'PAN Card, Aadhaar Card, Bank Statement',
    emi_advance_percentage: 50,
    emi_advance_message: 'Pay 50% split now, remaining amount in EMI',
    emi_advance_instructions: 'Split: 50% | Remaining: EMI for 50%',
    advance_payment_now_text: 'Split (Now)',
    advance_payment_after_demo_text: 'After Demo Completion',
    advance_payment_after_project_text: 'After Project Completion'
  });

  // Fetch payment options configuration
  const fetchPaymentOptions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await paymentOptionsAPI.getConfig();
      
      if (response?.data?.success) {
        const configData = {};
        Object.keys(response.data.data).forEach(key => {
          configData[key] = response.data.data[key].value;
        });
        setConfig(prev => ({ ...prev, ...configData }));
      }
    } catch (err) {
      console.error('Error fetching payment options:', err);
      setError('Failed to load payment options configuration');
    } finally {
      setLoading(false);
    }
  };

  // Save payment options configuration
  const savePaymentOptions = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await paymentOptionsAPI.updateConfig(config);
      
      if (response?.data?.success) {
        setSuccess('Payment options configuration saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to save payment options configuration');
      }
    } catch (err) {
      console.error('Error saving payment options:', err);
      setError(err.response?.data?.message || 'Failed to save payment options configuration');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchPaymentOptions();
  }, []);

  const handleInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePercentageChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue >= 0 && numValue <= 100) {
      setConfig(prev => ({
        ...prev,
        [field]: numValue
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Payment Options Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Configure payment options for customers (Full Payment vs Split Payment)
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 dark:text-green-200">{success}</span>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* General Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              General Settings
            </h2>
          </div>

          <div className="space-y-6">
            {/* Enable Payment Options */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Payment Options</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">Show payment options to customers</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.payment_options_enabled}
                  onChange={(e) => handleInputChange('payment_options_enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Section Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Section Title
              </label>
              <input
                type="text"
                value={config.payment_options_title}
                onChange={(e) => handleInputChange('payment_options_title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Choose Your Payment Option"
              />
            </div>

            {/* Section Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Section Description
              </label>
              <textarea
                value={config.payment_options_description}
                onChange={(e) => handleInputChange('payment_options_description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Select your preferred payment method and schedule"
              />
            </div>
          </div>
        </div>

        {/* Full Payment Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-center space-x-2 mb-6">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Full Payment Settings
            </h2>
          </div>

          <div className="space-y-6">
            {/* Enable Full Payment */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Full Payment</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">Allow customers to pay full amount upfront</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.full_payment_enabled}
                  onChange={(e) => handleInputChange('full_payment_enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Coupon Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Coupon Code
              </label>
              <input
                type="text"
                value={config.full_payment_coupon_code}
                onChange={(e) => handleInputChange('full_payment_coupon_code', e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                placeholder="FUL20"
                maxLength={10}
              />
            </div>

            {/* Coupon Discount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Discount Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={config.full_payment_coupon_discount}
                  onChange={(e) => handlePercentageChange('full_payment_coupon_discount', e.target.value)}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="20"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <Percent className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Coupon Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Coupon Message
              </label>
              <textarea
                value={config.full_payment_coupon_message}
                onChange={(e) => handleInputChange('full_payment_coupon_message', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Use coupon code FUL20 to get 20% discount on full payment"
              />
            </div>
          </div>
        </div>

        {/* Split Payment Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Split Payment Settings
            </h2>
          </div>

          <div className="space-y-6">
            {/* Enable Split Payment */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Split Payment</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">Allow customers to pay in installments</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.advance_payment_enabled}
                  onChange={(e) => handleInputChange('advance_payment_enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Split Payment Percentage */}
            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Advance Amount Percentage
                </label>
              <div className="relative">
                <input
                  type="number"
                  value={config.advance_payment_percentage}
                  onChange={(e) => handlePercentageChange('advance_payment_percentage', e.target.value)}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="50"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <Percent className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            {/* After Demo Completion Percentage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                After Demo Completion Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={config.advance_payment_after_demo_percentage}
                  onChange={(e) => handlePercentageChange('advance_payment_after_demo_percentage', e.target.value)}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="25"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <Percent className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            {/* After Project Completion Percentage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                After Project Completion Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={config.advance_payment_after_project_percentage}
                  onChange={(e) => handlePercentageChange('advance_payment_after_project_percentage', e.target.value)}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="25"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <Percent className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Payment Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Payment Instructions
              </label>
              <textarea
                value={config.advance_payment_instructions}
                onChange={(e) => handleInputChange('advance_payment_instructions', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Advance: 50% | After Demo Completion: 25% | After Project Completion: 25%"
              />
            </div>

            {/* Text Customization Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Text Customization</h4>
              
              {/* Advance (Now) Text */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Advance (Now) Text
                </label>
                <input
                  type="text"
                  value={config.advance_payment_now_text}
                  onChange={(e) => handleInputChange('advance_payment_now_text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Advance (Now)"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Text to display for the current split payment
                </p>
              </div>

              {/* After Demo Completion Text */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  After Demo Completion Text
                </label>
                <input
                  type="text"
                  value={config.advance_payment_after_demo_text}
                  onChange={(e) => handleInputChange('advance_payment_after_demo_text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="After Demo Completion"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Text to display for payment after demo completion
                </p>
              </div>

              {/* After Project Completion Text */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  After Project Completion Text
                </label>
                <input
                  type="text"
                  value={config.advance_payment_after_project_text}
                  onChange={(e) => handleInputChange('advance_payment_after_project_text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="After Project Completion"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Text to display for payment after project completion
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* EMI Payment Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-center space-x-2 mb-6">
            <CreditCardIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              EMI Payment Settings
            </h2>
          </div>

          <div className="space-y-6">
            {/* Enable EMI Payment */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable EMI Payment</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">Allow customers to pay in monthly installments</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.emi_payment_enabled}
                  onChange={(e) => handleInputChange('emi_payment_enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Minimum Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Minimum Order Amount for EMI
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={config.emi_minimum_amount}
                  onChange={(e) => handleInputChange('emi_minimum_amount', e.target.value)}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="1000"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-gray-400">₹</span>
                </div>
              </div>
            </div>

            {/* EMI Advance Percentage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                EMI Advance Payment Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={config.emi_advance_percentage}
                  onChange={(e) => handleInputChange('emi_advance_percentage', e.target.value)}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="50"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <Percent className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Percentage of total amount to be paid as advance before EMI</p>
            </div>

            {/* EMI Advance Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                EMI Advance Message
              </label>
              <input
                type="text"
                value={config.emi_advance_message}
                onChange={(e) => handleInputChange('emi_advance_message', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Pay 50% advance now, remaining amount in EMI"
              />
            </div>

            {/* EMI Advance Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                EMI Advance Instructions
              </label>
              <input
                type="text"
                value={config.emi_advance_instructions}
                onChange={(e) => handleInputChange('emi_advance_instructions', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Advance: 50% | Remaining: EMI for 50%"
              />
            </div>

            {/* EMI Tenures */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Available EMI Tenures (months)
              </label>
              <input
                type="text"
                value={config.emi_tenure_months}
                onChange={(e) => handleInputChange('emi_tenure_months', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="3,6,9,12"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Enter comma-separated values (e.g., 3,6,9,12)</p>
            </div>

            {/* Interest Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Interest Rate (per annum)
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={config.emi_interest_rate}
                  onChange={(e) => handleInputChange('emi_interest_rate', e.target.value)}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="15"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <Percent className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Processing Fee */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Processing Fee
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={config.emi_processing_fee}
                  onChange={(e) => handleInputChange('emi_processing_fee', e.target.value)}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="99"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-gray-400">₹</span>
                </div>
              </div>
            </div>

            {/* Eligibility Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Eligibility Message
              </label>
              <input
                type="text"
                value={config.emi_eligibility_message}
                onChange={(e) => handleInputChange('emi_eligibility_message', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="EMI available on orders above ₹1000"
              />
            </div>

            {/* Calculator Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Calculator Title
              </label>
              <input
                type="text"
                value={config.emi_calculator_title}
                onChange={(e) => handleInputChange('emi_calculator_title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="EMI Calculator"
              />
            </div>

            {/* Calculator Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Calculator Description
              </label>
              <textarea
                value={config.emi_calculator_description}
                onChange={(e) => handleInputChange('emi_calculator_description', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                 placeholder="Calculate your monthly EMI amount (Flat Rate Method)"
              />
            </div>

            {/* Bank Partners */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Partner Banks
              </label>
              <input
                type="text"
                value={config.emi_bank_partners}
                onChange={(e) => handleInputChange('emi_bank_partners', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="HDFC, ICICI, SBI, Axis, Kotak"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Enter comma-separated bank names</p>
            </div>

            {/* Required Documents */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Required Documents
              </label>
              <input
                type="text"
                value={config.emi_required_documents}
                onChange={(e) => handleInputChange('emi_required_documents', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="PAN Card, Aadhaar Card, Bank Statement"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Enter comma-separated document names</p>
            </div>

            {/* Terms and Conditions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Terms and Conditions
              </label>
              <textarea
                value={config.emi_terms_conditions}
                onChange={(e) => handleInputChange('emi_terms_conditions', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                 placeholder="EMI is subject to bank approval. Processing fee applies. Interest rate: 15% p.a. (Flat Rate Method)."
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={savePaymentOptions}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentOptionsManagement;
